/**
 * The menu's stylesheet, concatenated into the HUD's single `<style>` block
 * beside `INVENTORY_CSS`. Two families of class: `.mm-*` is the shell and the
 * kit (`kit.tsx`); the older `.meta*` and `.hide*` names are the controls the
 * traders, market and hideout screens are built from, restyled to match.
 *
 * One look: sans prose, mono numbers, warm gold for the thing to press, cards
 * that read by tone rather than outline, 120 ms transitions, a visible focus
 * ring on every control.
 */

const SANS = '"Segoe UI", Inter, system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const META_CSS = `
/* ── TOKENS ──────────────────────────────────────────────────────────────── */
/* On the HUD root too: the shelter's station panel draws kit pieces out in
   the raid HUD, outside .metamenu. */
#ui, .metamenu, .mm-scrim, .mm-auth {
  --mm-sans: ${SANS};
  --mm-mono: ${MONO};
  --mm-text: #e6ecf2;
  --mm-text-dim: rgba(230, 236, 242, 0.58);
  --mm-text-faint: rgba(230, 236, 242, 0.38);
  --mm-accent: #d6b06c;
  --mm-accent-hi: #ecc98a;
  --mm-accent-ink: #1b1a17;
  --mm-accent-wash: rgba(214, 176, 108, 0.14);
  --mm-good: #8fd39a;
  --mm-bad: #e08076;
  --mm-warn: #e0c184;
  --mm-surface: rgba(19, 22, 26, 0.86);
  --mm-surface-2: rgba(28, 32, 38, 0.92);
  --mm-hover: rgba(255, 255, 255, 0.045);
  --mm-line: rgba(214, 205, 194, 0.12);
  --mm-line-strong: rgba(214, 205, 194, 0.24);
  --mm-radius: 10px;
  --mm-radius-sm: 6px;
  --mm-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035), 0 10px 30px rgba(0, 0, 0, 0.35);
  --mm-ring: 0 0 0 2px rgba(236, 201, 138, 0.85);
  --mm-ease: 120ms cubic-bezier(0.2, 0.7, 0.2, 1);
}

/* ── THE SHELL ───────────────────────────────────────────────────────────── */
.metamenu {
  background-attachment: fixed;
  font: 13px/1.45 var(--mm-sans);
  color: var(--mm-text);
  -webkit-font-smoothing: antialiased;
}
/* A slim scrollbar wherever the menu scrolls. */
.metamenu *::-webkit-scrollbar { width: 8px; height: 8px; }
.metamenu *::-webkit-scrollbar-track { background: transparent; }
.metamenu *::-webkit-scrollbar-thumb {
  background: rgba(214, 205, 194, 0.16);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: padding-box;
}
.metamenu *::-webkit-scrollbar-thumb:hover { background-color: rgba(214, 205, 194, 0.28); }

/* The top bar: brand, tabs, player, deploy. One row, the tabs take the slack. */
.mm-top {
  display: flex;
  align-items: center;
  gap: 18px;
  height: 60px;
  padding: 0 22px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--mm-line);
  background: linear-gradient(180deg, rgba(14, 16, 19, 0.72), rgba(14, 16, 19, 0.38));
  backdrop-filter: blur(10px);
}
.mm-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  user-select: none;
}
.mm-brand-mark {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: var(--mm-accent-ink);
  background: linear-gradient(160deg, var(--mm-accent-hi), var(--mm-accent));
  font: 800 13px/1 var(--mm-sans);
  letter-spacing: -0.02em;
  box-shadow: 0 4px 14px rgba(214, 176, 108, 0.28);
}
.mm-brand-name {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.mm-brand-name b {
  font: 700 13px/1.1 var(--mm-sans);
  letter-spacing: 0.02em;
}
.mm-brand-name small {
  font: 10px/1.1 var(--mm-sans);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--mm-text-faint);
}

/* The tabs: a segmented strip. Icon, label, and the digit that jumps to it. */
.mm-tabs {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
  padding: 3px;
  border-radius: var(--mm-radius);
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid var(--mm-line);
  overflow-x: auto;
  scrollbar-width: none;
}
.mm-tabs::-webkit-scrollbar { display: none; }
.mm-tab, .metatab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 7px;
  color: var(--mm-text-dim);
  background: transparent;
  font: 500 12.5px/1 var(--mm-sans);
  letter-spacing: 0.01em;
  cursor: pointer;
  white-space: nowrap;
  transition: color var(--mm-ease), background var(--mm-ease), box-shadow var(--mm-ease);
}
.mm-tab:hover, .metatab:hover {
  color: var(--mm-text);
  background: var(--mm-hover);
}
.mm-tab[aria-selected="true"], .metatab[aria-selected="true"] {
  color: var(--mm-accent-hi);
  background: var(--mm-accent-wash);
  box-shadow: inset 0 0 0 1px rgba(214, 176, 108, 0.32);
}
.mm-tab svg { flex: 0 0 auto; opacity: 0.85; }
.mm-tab[aria-selected="true"] svg { opacity: 1; }
.mm-tab-key {
  display: inline-grid;
  place-items: center;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  margin-left: 2px;
  border-radius: 3px;
  color: var(--mm-text-faint);
  background: rgba(255, 255, 255, 0.06);
  font: 600 9px/1 var(--mm-mono);
  transition: color var(--mm-ease), background var(--mm-ease);
}
.mm-tab[aria-selected="true"] .mm-tab-key {
  color: var(--mm-accent-ink);
  background: var(--mm-accent);
}
/* The digit hints only show once the keyboard is in use, or on hover. */
.mm-tab .mm-tab-key { opacity: 0; }
.mm-tabs:hover .mm-tab-key, .metamenu[data-keys="true"] .mm-tab-key { opacity: 1; }

/* The player chip: avatar, name and level, wallet beside it. */
.mm-player {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  height: 38px;
  padding: 0 12px 0 5px;
  border-radius: var(--mm-radius);
  border: 1px solid var(--mm-line);
  background: rgba(0, 0, 0, 0.28);
}
.mm-avatar {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: var(--mm-text);
  background: linear-gradient(160deg, rgba(96, 124, 156, 0.7), rgba(56, 70, 90, 0.7));
  font: 700 12px/1 var(--mm-sans);
  text-transform: uppercase;
}
.mm-player-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  line-height: 1.1;
}
.mm-player-name b {
  font: 600 12.5px/1.1 var(--mm-sans);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mm-player-name small {
  font: 10.5px/1.1 var(--mm-sans);
  color: var(--mm-text-dim);
}
.mm-wallet {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px;
  border-radius: var(--mm-radius);
  border: 1px solid rgba(214, 176, 108, 0.22);
  background: var(--mm-accent-wash);
  color: var(--mm-accent-hi);
  font: 600 13px/1 var(--mm-mono);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.mm-wallet svg { opacity: 0.8; }

/* The body of the screen. */
.mm-main {
  flex: 1;
  min-height: 0;
  padding: 18px 22px 22px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  overflow: hidden;
}
/* A tab's content arrives, rather than appears. */
.mm-page {
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 0;
  animation: mm-page-in 160ms cubic-bezier(0.2, 0.7, 0.2, 1);
}
/* Under ~1360 px the strip would scroll; the names go first. */
@media (max-width: 1360px) {
  .mm-brand-name, .mm-player-name { display: none; }
  .mm-player { padding-right: 5px; }
}
/* The header's right-hand group: chip, wallet, sign out, deploy. */
.mm-top-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}
@keyframes mm-page-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .mm-page, .hidepanel, .mm-modal { animation: none; }
  .metamenu *, .mm-scrim * { transition: none !important; }
}

/* ── BUTTONS ─────────────────────────────────────────────────────────────── */
/* The one warm button: the thing that starts something. */
.metadeploy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 38px;
  padding: 0 20px;
  border: 0;
  border-radius: var(--mm-radius);
  color: var(--mm-accent-ink);
  background: linear-gradient(180deg, var(--mm-accent-hi), var(--mm-accent));
  box-shadow: 0 6px 18px rgba(214, 176, 108, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.35);
  font: 700 13px/1 var(--mm-sans);
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  transition: transform var(--mm-ease), box-shadow var(--mm-ease), filter var(--mm-ease), opacity var(--mm-ease);
}
.metadeploy:hover:enabled {
  filter: brightness(1.06);
  box-shadow: 0 8px 24px rgba(214, 176, 108, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.35);
  transform: translateY(-1px);
}
.metadeploy:active:enabled { transform: translateY(0); filter: brightness(0.98); }
.metadeploy:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
.metadeploy kbd, .metadeploy .mm-kbd {
  color: rgba(27, 26, 23, 0.8);
  background: rgba(0, 0, 0, 0.12);
  border-color: rgba(0, 0, 0, 0.18);
}
/* Buy, sell, cancel, back: the ordinary button. */
.metabtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid var(--mm-line-strong);
  border-radius: var(--mm-radius-sm);
  color: var(--mm-text);
  background: rgba(255, 255, 255, 0.04);
  font: 500 12px/1 var(--mm-sans);
  cursor: pointer;
  white-space: nowrap;
  transition: color var(--mm-ease), background var(--mm-ease), border-color var(--mm-ease);
}
.metabtn:hover:enabled {
  background: rgba(255, 255, 255, 0.09);
  border-color: rgba(214, 205, 194, 0.38);
}
.metabtn:active:enabled { background: rgba(255, 255, 255, 0.06); }
.metabtn:disabled { opacity: 0.38; cursor: not-allowed; }
/* A ghost variant: no fill until hovered, for a row's secondary action. */
.metabtn-ghost { border-color: transparent; background: transparent; }
/* A small variant, for inside rows. */
.metabtn-sm { height: 26px; padding: 0 9px; font-size: 11.5px; }
/* A danger variant. */
.metabtn-danger:hover:enabled { color: #ffb4ab; border-color: rgba(224, 128, 118, 0.5); background: rgba(224, 128, 118, 0.12); }
/* An icon-only button. */
.metabtn-icon { width: 30px; padding: 0; }

/* Every control gets the same ring, and only from the keyboard. */
.metamenu button:focus-visible, .metamenu input:focus-visible, .metamenu [tabindex]:focus-visible,
.mm-scrim button:focus-visible, .mm-scrim input:focus-visible,
.mm-auth button:focus-visible, .mm-auth input:focus-visible {
  outline: none;
  box-shadow: var(--mm-ring);
}

/* ── FIELDS ──────────────────────────────────────────────────────────────── */
.metainput {
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 0 10px;
  box-sizing: border-box;
  border: 1px solid var(--mm-line-strong);
  border-radius: var(--mm-radius-sm);
  color: var(--mm-text);
  background: rgba(0, 0, 0, 0.32);
  font: 12.5px/1 var(--mm-sans);
  transition: border-color var(--mm-ease), background var(--mm-ease);
}
.metainput[type="number"] { font-family: var(--mm-mono); font-variant-numeric: tabular-nums; }
.metainput::placeholder { color: var(--mm-text-faint); }
.metainput:focus {
  outline: none;
  border-color: rgba(214, 176, 108, 0.6);
  background: rgba(0, 0, 0, 0.44);
}
/* A field with a leading icon: wrap both in .mm-field. */
.mm-field { position: relative; display: flex; align-items: center; flex: 1 1 auto; min-width: 0; }
.mm-card-tight > .mm-field, .mm-split-aside > .mm-field { flex: 0 0 auto; }
.mm-field > svg { position: absolute; left: 10px; color: var(--mm-text-faint); pointer-events: none; }
.mm-field > .metainput { padding-left: 32px; }
.mm-field > .mm-field-clear {
  position: absolute; right: 4px;
  width: 24px; height: 24px; padding: 0;
  display: grid; place-items: center;
  border: 0; border-radius: 5px;
  color: var(--mm-text-dim); background: transparent; cursor: pointer;
}
.mm-field > .mm-field-clear:hover { color: var(--mm-text); background: var(--mm-hover); }

/* The range slider, restyled to the accent. */
.mm-range {
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  min-width: 0;
  height: 22px;
  background: transparent;
  cursor: pointer;
}
.mm-range::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--mm-accent) var(--mm-range-fill, 0%), rgba(255, 255, 255, 0.14) var(--mm-range-fill, 0%));
}
.mm-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  margin-top: -6px;
  border-radius: 50%;
  border: 2px solid var(--mm-accent-hi);
  background: #1b1a17;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  transition: transform var(--mm-ease);
}
.mm-range:hover::-webkit-slider-thumb { transform: scale(1.1); }
.mm-range::-moz-range-track { height: 4px; border-radius: 2px; background: rgba(255, 255, 255, 0.14); }
.mm-range::-moz-range-progress { height: 4px; border-radius: 2px; background: var(--mm-accent); }
.mm-range::-moz-range-thumb {
  width: 12px; height: 12px; border-radius: 50%;
  border: 2px solid var(--mm-accent-hi); background: #1b1a17;
}

/* The switch: a real checkbox, hidden, driving a track. */
.mm-switch {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}
.mm-switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.mm-switch-track {
  position: relative;
  width: 34px;
  height: 20px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid var(--mm-line-strong);
  box-sizing: border-box;
  transition: background var(--mm-ease), border-color var(--mm-ease);
}
.mm-switch-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #f4f8fc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  transition: transform var(--mm-ease), background var(--mm-ease);
}
.mm-switch input:checked + .mm-switch-track { background: var(--mm-accent); border-color: var(--mm-accent-hi); }
.mm-switch input:checked + .mm-switch-track .mm-switch-knob { transform: translateX(14px); background: var(--mm-accent-ink); }
.mm-switch input:focus-visible + .mm-switch-track { box-shadow: var(--mm-ring); }
.mm-switch input:disabled ~ * { opacity: 0.45; cursor: not-allowed; }
.mm-switch-label { font: 12.5px/1.3 var(--mm-sans); }

/* ── THE KIT ─────────────────────────────────────────────────────────────── */
.mm-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px;
  box-sizing: border-box;
  border-radius: var(--mm-radius);
  border: 1px solid var(--mm-line);
  background: var(--mm-surface);
  box-shadow: var(--mm-shadow);
  min-width: 0;
}
.mm-card-tight { padding: 10px; gap: 6px; }
/* A card inside a detail column: raised off the card it sits on. */
.mm-card-nested { background: var(--mm-surface-2); }
/* A card inside a card: lighter still, no lift. */
.mm-card-inset { background: rgba(255, 255, 255, 0.03); box-shadow: none; }
.mm-card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 18px;
}
.mm-card-aside {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--mm-text-dim);
  font: 12px/1 var(--mm-mono);
  font-variant-numeric: tabular-nums;
}
.mm-eyebrow {
  font: 600 10.5px/1.2 var(--mm-sans);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--mm-text-dim);
}
.mm-title { font: 600 15px/1.25 var(--mm-sans); letter-spacing: -0.005em; color: var(--mm-text); }
.mm-title-lg { font-size: 20px; }
.mm-title-xl { font-size: 28px; font-weight: 700; letter-spacing: -0.01em; }
.mm-note { font: 12px/1.5 var(--mm-sans); color: var(--mm-text-dim); }
.mm-num { font-family: var(--mm-mono); font-variant-numeric: tabular-nums; white-space: nowrap; }
.mm-tone-accent { color: var(--mm-accent-hi); }
.mm-tone-good { color: var(--mm-good); }
.mm-tone-bad { color: var(--mm-bad); }
.mm-tone-dim { color: var(--mm-text-dim); }

.mm-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px;
  border-radius: 10px;
  font: 600 10.5px/1 var(--mm-sans);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  flex: 0 0 auto;
}
.mm-badge-accent { color: var(--mm-accent-hi); background: var(--mm-accent-wash); box-shadow: inset 0 0 0 1px rgba(214, 176, 108, 0.32); }
.mm-badge-good { color: var(--mm-good); background: rgba(143, 211, 154, 0.12); box-shadow: inset 0 0 0 1px rgba(143, 211, 154, 0.3); }
.mm-badge-bad { color: var(--mm-bad); background: rgba(224, 128, 118, 0.12); box-shadow: inset 0 0 0 1px rgba(224, 128, 118, 0.3); }
.mm-badge-warn { color: var(--mm-warn); background: rgba(224, 193, 132, 0.1); box-shadow: inset 0 0 0 1px rgba(224, 193, 132, 0.3); }
.mm-badge-neutral { color: var(--mm-text-dim); background: rgba(255, 255, 255, 0.06); box-shadow: inset 0 0 0 1px var(--mm-line-strong); }

.mm-bar {
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.mm-bar-thin { height: 4px; }
.mm-bar-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--mm-accent), var(--mm-accent-hi));
  transition: width 240ms cubic-bezier(0.2, 0.7, 0.2, 1);
}
.mm-bar-done { background: linear-gradient(90deg, #6fbf7c, var(--mm-good)); }

.mm-stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: var(--mm-radius-sm);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--mm-line);
  min-width: 0;
}
.mm-stat-value {
  font: 600 22px/1.1 var(--mm-mono);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.mm-kv {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 12.5px;
}
.mm-kv-label { color: var(--mm-text-dim); }
.mm-kv-value { font-family: var(--mm-mono); font-variant-numeric: tabular-nums; white-space: nowrap; }

.mm-kbd, .metacounter-sell kbd {
  display: inline-grid;
  place-items: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 4px;
  color: var(--mm-text-dim);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--mm-line-strong);
  font: 600 9.5px/1 var(--mm-mono);
  text-transform: uppercase;
  vertical-align: middle;
}
.metacounter-sell kbd { margin-left: 8px; }
.mm-divider { height: 1px; background: var(--mm-line); }

.mm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 160px;
  padding: 24px;
  text-align: center;
  color: var(--mm-text-dim);
}
.mm-empty-icon { color: var(--mm-text-faint); margin-bottom: 4px; }
.mm-empty-icon svg { width: 28px; height: 28px; }
.mm-empty-title { font: 600 13.5px/1.3 var(--mm-sans); color: var(--mm-text); }
.mm-empty-action { margin-top: 8px; }

/* Modals: scrim, blur, a card. Fixed, so a scrim opened from inside a card
   (the quest handover) still covers the screen — #ui is transformed, which
   makes fixed resolve against it, i.e. the whole viewport. */
.mm-scrim {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 7, 9, 0.66);
  backdrop-filter: blur(6px);
  font: 13px/1.45 var(--mm-sans);
  color: var(--mm-text);
  pointer-events: auto;
  user-select: none;
  z-index: 65;
}
.mm-modal {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);
  padding: 22px 24px;
  box-sizing: border-box;
  overflow-y: auto;
  border-radius: 14px;
  border: 1px solid var(--mm-line-strong);
  background: rgb(19, 22, 26);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  animation: mm-modal-in 160ms cubic-bezier(0.2, 0.7, 0.2, 1);
}
@keyframes mm-modal-in {
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to   { opacity: 1; transform: none; }
}

/* The split page: sidebar + scrolling detail. */
.mm-split {
  display: flex;
  gap: 14px;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
}
.mm-split-aside { display: flex; flex-direction: column; min-height: 0; overflow-y: auto; }
.mm-split-main { flex: 1; min-width: 0; min-height: 0; overflow-y: auto; }

/* A sidebar row: trader, giver, module, category. Lit when selected. */
.mm-row, .metatrader {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  box-sizing: border-box;
  border: 0;
  border-radius: var(--mm-radius-sm);
  color: var(--mm-text);
  background: transparent;
  font: 12.5px/1.3 var(--mm-sans);
  text-align: left;
  cursor: pointer;
  transition: background var(--mm-ease), box-shadow var(--mm-ease);
}
.mm-row:hover:enabled, .metatrader:hover:enabled { background: var(--mm-hover); }
.mm-row[aria-selected="true"], .metatrader[aria-selected="true"] {
  background: var(--mm-accent-wash);
  box-shadow: inset 2px 0 0 var(--mm-accent);
}
.mm-row:disabled, .metatrader:disabled { cursor: default; opacity: 0.6; }
.mm-row-lead { flex: 0 0 auto; display: grid; place-items: center; }
.mm-row-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.mm-row-title { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mm-row-sub { font-size: 11px; color: var(--mm-text-dim); line-height: 1.35; }
.mm-row-trail { flex: 0 0 auto; color: var(--mm-text-dim); font: 11.5px/1 var(--mm-mono); font-variant-numeric: tabular-nums; }

/* A table: the leaderboard. */
.mm-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.mm-table th {
  padding: 6px 10px;
  text-align: left;
  font: 600 10.5px/1.2 var(--mm-sans);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--mm-text-dim);
  border-bottom: 1px solid var(--mm-line);
}
.mm-table td {
  padding: 7px 10px;
  border-bottom: 1px solid var(--mm-line);
  font-variant-numeric: tabular-nums;
}
.mm-table td.mm-num, .mm-table th.mm-num { text-align: right; font-family: var(--mm-mono); }
.mm-table tr:last-child td { border-bottom: 0; }
.mm-table tbody tr:hover td { background: var(--mm-hover); }
.mm-table tr[data-me="true"] td { background: var(--mm-accent-wash); color: var(--mm-accent-hi); }

/* Portraits: every trader picture gets the same rounding. */
.mm-portrait { display: flex; border-radius: 8px; overflow: hidden; flex: 0 0 auto; }
.mm-portrait > img, .mm-portrait > * { border-radius: inherit; border: 0; }

/* ── THE SHELF (traders tab) ─────────────────────────────────────────────── */
/* One thing on the shelf: a tile first, a button for the keyboard. Never
   disabled — an unaffordable offer is still one you may inspect. */
.metaoffer {
  display: block;
  margin: 0;
  padding: 0;
  color: inherit;
  background: transparent;
  border: 0;
  font: inherit;
  text-align: left;
  cursor: grab;
}
.metaoffer:hover > div {
  filter: brightness(1.25);
  box-shadow: inset 0 0 0 1px rgba(238, 205, 148, 0.55);
}
.metaoffer:focus-visible {
  outline: 2px solid rgba(238, 205, 148, 0.9);
  outline-offset: -2px;
  z-index: 2;
}
/* The gates, stacked in the corner the name, price and stack badge leave. */
.metaoffer-gates {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 1;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.metaoffer-gate {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 4px;
  border-radius: 3px;
  color: rgba(227, 234, 241, 0.5);
  background: rgba(5, 6, 7, 0.78);
  font: 600 9px/1 var(--mm-mono);
  white-space: nowrap;
}
.metaoffer-gate-shut { color: #f0d39c; }
.metaoffer-price {
  padding: 1px 4px;
  border-radius: 3px;
  color: #f0d39c;
  background: rgba(5, 6, 7, 0.78);
  font: 600 9px/1 var(--mm-mono);
  white-space: nowrap;
}

/* THE SELL COUNTER: a drop target and a list; nothing sells until the button. */
.metacounter {
  box-sizing: border-box;
  padding: 10px;
  border: 1px dashed var(--mm-line-strong);
  border-radius: var(--mm-radius-sm);
  background: rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: background var(--mm-ease), border-color var(--mm-ease);
}
.metacounter[data-active="true"][data-allowed="true"] {
  border-style: solid;
  border-color: rgba(140, 220, 148, 0.82);
  background: rgba(112, 190, 120, 0.18);
}
.metacounter[data-active="true"][data-allowed="false"] {
  border-style: solid;
  border-color: rgba(232, 104, 92, 0.82);
  background: rgba(212, 78, 68, 0.18);
}
.metacounter-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font: 600 10.5px/1.2 var(--mm-sans);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--mm-text-dim);
}
.metacounter-head > span:last-child { font: 600 12px/1 var(--mm-mono); letter-spacing: 0; text-transform: none; }
.metacounter-hint {
  font: 11.5px/1.5 var(--mm-sans);
  color: var(--mm-text-dim);
  padding: 0 2px;
}
.metacounter-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 168px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.metacounter-row {
  display: flex;
  width: 100%;
  align-items: baseline;
  gap: 6px;
  font: 12px/1.3 var(--mm-sans);
  color: var(--mm-text);
  background: transparent;
  border: 0;
  border-radius: 4px;
  padding: 4px 6px;
  text-align: left;
  cursor: pointer;
  transition: background var(--mm-ease);
}
.metacounter-row:hover:enabled { background: var(--mm-hover); }
.metacounter-row:disabled { opacity: 0.5; cursor: default; }
.metacounter-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.metacounter-price { color: #f0d39c; white-space: nowrap; font-family: var(--mm-mono); font-variant-numeric: tabular-nums; }
.metacounter-x { color: var(--mm-text-faint); }
.metacounter-row:hover:enabled .metacounter-x { color: var(--mm-text); }
.metacounter-sell { width: 100%; font-size: 12.5px; height: 34px; }
.metacounter-sell:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── THE MARKET ──────────────────────────────────────────────────────────── */
/* The five men plus all of them: a filter strip under the tab strip. */
.metasubtab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 11px;
  border: 0;
  border-radius: 15px;
  color: var(--mm-text-dim);
  background: transparent;
  font: 500 12px/1 var(--mm-sans);
  cursor: pointer;
  white-space: nowrap;
  transition: color var(--mm-ease), background var(--mm-ease), box-shadow var(--mm-ease);
}
.metasubtab:hover { color: var(--mm-text); background: var(--mm-hover); }
.metasubtab[aria-selected="true"] {
  color: var(--mm-accent-hi);
  background: var(--mm-accent-wash);
  box-shadow: inset 0 0 0 1px rgba(214, 176, 108, 0.32);
}
/* One node of the category tree. */
.metacat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 30px;
  padding: 0 10px;
  box-sizing: border-box;
  border: 0;
  border-radius: var(--mm-radius-sm);
  color: var(--mm-text);
  background: transparent;
  font: 12.5px/1 var(--mm-sans);
  text-align: left;
  cursor: pointer;
  transition: background var(--mm-ease), box-shadow var(--mm-ease);
}
.metacat[data-depth="1"] { padding-left: 24px; font-size: 12px; }
.metacat:hover:enabled { background: var(--mm-hover); }
.metacat[aria-selected="true"] { background: var(--mm-accent-wash); box-shadow: inset 2px 0 0 var(--mm-accent); color: var(--mm-accent-hi); }
.metacat[data-empty="true"] { color: var(--mm-text-faint); }
.metacat-count { color: var(--mm-text-dim); font: 11px/1 var(--mm-mono); font-variant-numeric: tabular-nums; }
.metacat[aria-selected="true"] .metacat-count { color: var(--mm-accent-hi); }
/* The focus chip: what the list was narrowed to, and the way out. */
.metafocus {
  display: flex;
  align-items: center;
  gap: 8px;
  font: 12px/1 var(--mm-sans);
  padding: 8px 10px;
  border-bottom: 1px solid var(--mm-line);
}
/* One line of the market: picture, name, trader, price, button. */
.metarow {
  display: grid;
  grid-template-columns: 64px 1fr 160px 96px 96px;
  align-items: center;
  gap: 12px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--mm-line);
  font-size: 12.5px;
  transition: background var(--mm-ease);
}
.metarow:hover { background: var(--mm-hover); }
.metarow[aria-selected="true"] { background: var(--mm-accent-wash); }
.metarow[data-dimmed="true"] > :not(.metarow-buy) { opacity: 0.5; }
.metarow-head {
  position: sticky;
  top: 0;
  z-index: 1;
  font: 600 10.5px/1.2 var(--mm-sans);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--mm-text-dim);
  background: rgba(19, 22, 26, 0.98);
  padding: 9px 10px;
}
.metarow-head:hover { background: rgba(19, 22, 26, 0.98); }
.metarow-sort {
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  transition: color var(--mm-ease);
}
.metarow-sort:hover, .metarow-sort[aria-pressed="true"] { color: var(--mm-text); }

/* ── THE SHELTER ─────────────────────────────────────────────────────────── */
.hidepanel {
  animation: rietz-hidepanel 90ms ease-out;
  font: 13px/1.45 var(--mm-sans);
  color: var(--mm-text);
}
@keyframes rietz-hidepanel {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
}
/* One of the two skills the gym teaches: a button grown to a card. */
.hidepick {
  flex: 1 1 180px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  font: 12.5px/1.35 var(--mm-sans);
  color: var(--mm-text);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--mm-line-strong);
  border-radius: var(--mm-radius-sm);
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
  transition: background var(--mm-ease), border-color var(--mm-ease);
}
.hidepick:hover:enabled { background: rgba(255, 255, 255, 0.07); border-color: rgba(214, 176, 108, 0.5); }
.hidepick:disabled { opacity: 0.45; cursor: not-allowed; }
/* The set itself: one big button, instant press state. */
.hidepress {
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
  font: 12.5px/1.35 var(--mm-sans);
  color: var(--mm-text);
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid var(--mm-line-strong);
  border-radius: var(--mm-radius-sm);
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
}
.hidepress:hover { border-color: rgba(214, 176, 108, 0.5); }
.hidepress:active { background: var(--mm-accent-wash); }
`;
